/* One visibility gate for homepage background videos, including hidden mobile
 * copies. Card gesture logic retains playback ownership below 769px. */
(() => {
  if (window.mwyBackgroundVideoReady || !document.body.classList.contains('template-index') || !('IntersectionObserver' in window)) return;
  window.mwyBackgroundVideoReady = true;
  const desktop = matchMedia('(min-width: 769px)');
  const selector = '.video-parent-section video.video-div, video.mwy-album-card__video, video.mwy-mobile-vol2-video';
  const states = new Map();
  function managed(video) {
    return desktop.matches || video.matches('.video-parent-section video.video-div');
  }
  function visible(video) {
    if (document.hidden || !video.isConnected) return false;
    const rect = video.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.top < innerHeight && rect.right > 0 && rect.left < innerWidth;
  }
  function sync(video, state) {
    if (!managed(video) || video.controls) return;
    const shouldPlay = state.intersecting && visible(video);
    if (!shouldPlay) {
      if (state.startFrame) cancelAnimationFrame(state.startFrame);
      state.startFrame = 0;
      if (!video.paused) video.pause();
      return;
    }
    if (video.dataset.mwyVideoSrc) {
      if (!state.startFrame) {
        // The still and scrollable page get a paint before media loading or
        // decoding starts. Never wait for loadeddata/canplay/window.load.
        state.startFrame = requestAnimationFrame(() => {
          state.startFrame = requestAnimationFrame(() => {
            state.startFrame = 0;
            if (!state.intersecting || !visible(video)) return;
            video.preload = 'metadata';
            video.src = video.dataset.mwyVideoSrc;
            delete video.dataset.mwyVideoSrc;
            video.closest('.video-parent-section').classList.add('loaded');
            sync(video, state);
          });
        });
      }
      return;
    }
    if (video.paused && !state.pending) {
      const play = video.play();
      if (play && typeof play.then === 'function') {
        state.pending = true;
        play.catch(error => {
          // A policy rejection needs a usable play button, not a blank hero.
          // AbortError is expected when scrolling away during startup.
          if (state.deferredHero && error.name === 'NotAllowedError') {
            video.controls = true;
            video.closest('.video-parent-section').classList.add('video-interactable');
          }
        }).finally(() => {
          state.pending = false;
          if (managed(video) && !video.controls && (!state.intersecting || !visible(video))) video.pause();
        });
      }
    }
  }
  const observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      const state = states.get(entry.target);
      if (!state) return;
      state.intersecting = entry.isIntersecting && entry.intersectionRatio > 0;
      sync(entry.target, state);
    });
  }, { rootMargin: '0px', threshold: 0 });
  function scan(root = document) {
    root.querySelectorAll(selector).forEach(video => {
      if (states.has(video)) return;
      const state = { intersecting: false, pending: false, startFrame: 0, deferredHero: !!video.dataset.mwyVideoSrc };
      states.set(video, state);
      // A theme initializer can call play() after the observer has paused a
      // hidden video. Recheck every such request, not only viewport crossings.
      video.addEventListener('play', () => {
        if (managed(video) && !video.controls && (!state.intersecting || !visible(video))) video.pause();
      });
      video.preload = state.deferredHero ? 'none' : 'metadata';
      observer.observe(video);
      if (managed(video) && !video.controls && !visible(video)) video.pause();
    });
  }
  function syncAll() {
    states.forEach((state, video) => {
      if (!video.isConnected) {
        if (state.startFrame) cancelAnimationFrame(state.startFrame);
        observer.unobserve(video);
        states.delete(video);
      }
      else sync(video, state);
    });
  }
  scan();
  desktop.addEventListener('change', syncAll);
  document.addEventListener('visibilitychange', syncAll);
  window.addEventListener('pageshow', syncAll);
  document.addEventListener('shopify:section:load', event => { scan(event.target); syncAll(); });
  document.addEventListener('shopify:section:unload', syncAll);
})();
